import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { getProducts } from "@/lib/site";
import { Reveal, SectionTag } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";

export default function Products() {
  const [products, setProducts] = useState([]);
  const { add } = useCart();
  const navigate = useNavigate();
  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
  }, []);

  return (
    <section
      id="shop"
      data-testid="products-section"
      className="border-y border-white/10 bg-moss px-6 py-24 lg:px-16 lg:py-36"
    >
      <Reveal>
        <SectionTag index="03" label="Butikken" />
        <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
          <h2 className="max-w-xl font-serif text-4xl tracking-tight text-cream sm:text-5xl">
            Foder, jord &amp; udstyr
          </h2>
          <p className="max-w-xs font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-sage/70">
            Gratis fragt ved køb over 499 kr · op til 10 kg
          </p>
        </div>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {products.length === 0 &&
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-72 animate-pulse border border-white/10 bg-surface" />
          ))}
        {products.map((p, i) => {
          const soldOut = (p.inventory_qty ?? 1) <= 0;
          return (
            <Reveal key={p.id} delay={(i % 4) * 0.06}>
              <div
                data-testid={`product-card-${p.slug}`}
                onClick={() => navigate(`/vare/${p.slug}`)}
                className="group flex h-full cursor-pointer flex-col border border-white/10 bg-surface transition-colors duration-300 hover:border-forest/60"
              >
                <div className="relative flex h-48 items-center justify-center overflow-hidden bg-[#EFEBE3] p-6">
                  <img
                    src={p.image}
                    alt={p.name}
                    className={`max-h-full object-contain mix-blend-multiply transition-transform duration-500 group-hover:scale-105 ${
                      soldOut ? "opacity-40" : ""
                    }`}
                  />
                  {soldOut && (
                    <span className="absolute border border-bark/60 bg-ink/80 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-bark">
                      Udsolgt
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-pale/70">
                    {p.category}
                  </p>
                  <h3 className="mt-2 flex-1 text-sm font-medium leading-snug text-cream">
                    {p.name}
                  </h3>
                  <p className="mt-4 font-mono text-xs tracking-[0.15em] text-pale">
                    {p.price}
                  </p>
                  {soldOut ? (
                    <p
                      data-testid={`product-soldout-${p.slug}`}
                      className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-sage"
                    >
                      Udsolgt
                    </p>
                  ) : (
                    <button
                      data-testid={`product-add-${p.slug}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        add({ slug: p.slug, name: p.name, price_dkk: p.price_dkk, image: p.image, weight_kg: p.weight_kg, no_postal: p.no_postal || undefined });
                      }}
                      className="mt-4 flex items-center justify-center gap-2 rounded-full border border-white/20 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:border-forest hover:bg-forest"
                    >
                      <ShoppingBag className="h-3.5 w-3.5" />
                      Læg i kurv
                    </button>
                  )}
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}
